import React from "react"
import { brandPalette } from "../theme"

type BrandLoaderProps = {
    isLoading: boolean
    label?: string
    /** overlay = covers the whole viewport; inline (default) = centered block in normal flow */
    fullScreen?: boolean
}

const KEYFRAMES = `
@keyframes fpl-spin   { to   { transform: rotate(360deg); } }
@keyframes fpl-spin-rev { to { transform: rotate(-360deg); } }
@keyframes fpl-pulse  { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
@keyframes fpl-fade   { from { opacity: 0; } to { opacity: 1; } }
`

export function BrandLoader({ isLoading, label, fullScreen = false }: BrandLoaderProps) {
    if (!isLoading) return null

    const overlayStyle: React.CSSProperties = fullScreen
        ? {
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(240, 244, 247, 0.72)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
        }
        : {
            width: "100%",
            padding: "48px 0",
        }

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 22,
                animation: "fpl-fade .25s ease-out",
                ...overlayStyle,
            }}
        >
            <style>{KEYFRAMES}</style>

            <div style={{ position: "relative", width: 64, height: 64 }}>
                {/* outer ring */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        border: "3px solid rgba(13, 92, 107, 0.15)",
                        borderTopColor: brandPalette.primary,
                        animation: "fpl-spin 1s linear infinite",
                    }}
                />
                {/* inner counter-rotating ring */}
                <div
                    style={{
                        position: "absolute",
                        inset: 12,
                        borderRadius: "50%",
                        border: "3px solid rgba(0, 151, 167, 0.12)",
                        borderBottomColor: brandPalette.secondary,
                        animation: "fpl-spin-rev 1.4s linear infinite",
                    }}
                />
                {/* core dot */}
                <div
                    style={{
                        position: "absolute",
                        inset: 26,
                        borderRadius: "50%",
                        background: brandPalette.primary,
                        animation: "fpl-pulse 1.2s ease-in-out infinite",
                    }}
                />
            </div>

            {label && (
                <span
                    style={{
                        color: brandPalette.textSecondary,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 14,
                        fontWeight: 500,
                        letterSpacing: 0.3,
                    }}
                >
                    {label}
                </span>
            )}
        </div>
    )
}
